import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import axios, { CancelTokenSource } from 'axios';
import AddButton from 'components/AddButton';
import DeleteActionButton from 'components/DeleteActionButton';
import EditActionButton from 'components/EditActionButton';
import CheckBox from 'components/final-form/CheckBox';
import { DropdownOption, mapToDropdownOptionArrayWithSep } from 'components/final-form/dropdownUtils';
import Input from 'components/final-form/Input';
import Select from 'components/final-form/Select';
import UpsertDivisionForm from 'components/guarantors/division/UpsertDivisionForm';
import { GuarantorsConsumerRenderProps, withGuarantorsContext } from 'components/guarantors/GuarantorsContext';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import PxGrid from 'components/layout/PxGrid';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import SearchResultComponent from 'components/SearchResultComponent';
import { FormState } from 'final-form';
import React, { Component } from 'react';
import { Field, Form as FinalForm, FormRenderProps, FormSpy } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { Grid, Modal } from 'semantic-ui-react';
import { getAllCompanies } from 'services/companyServices';
import { getAllCountries } from 'services/countryServices';
import { deleteDivision, findDivisionById, searchDivisions } from 'services/divisionServices';
import { debounce } from 'ts-debounce';
import { CountryDto, DivisionDto, DivisionSearchRequest, UpsertDivisionDto } from 'ts-types/api.types';
import { noop } from 'utils/functionUtils';

interface DivisionFieldProps extends FormState {
  values: Partial<DivisionSearchRequest>
}

interface Props extends MessageSourceProps, GuarantorsConsumerRenderProps, AuthConsumerRenderProps {
}

interface State {
  referenceDataLoaded: boolean,
  searchValues: Partial<DivisionSearchRequest>,
  companies: Array<DropdownOption>,
  countries: Array<CountryDto>,

  foundDivisions: Array<DivisionDto>,
  divisionsLoaded: boolean,

  addEditModalOpen: boolean,
  division?: UpsertDivisionDto,
  divisionId?: number,

  cancelTokenSource: CancelTokenSource,
}

class DivisionsView extends Component<Props, State> {

  private mounted: boolean = false;

  private initialSearchValues: Partial<DivisionSearchRequest> = {
    companyId: undefined,
    searchKey: "",
    includesInactive: false
  }

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.initialSearchValues.companyId = props.selectedCompanyId;

    this.state = {
      referenceDataLoaded: false,

      searchValues: {},
      companies: [],
      countries: [],

      foundDivisions: [],
      divisionsLoaded: false,

      addEditModalOpen: false,

      cancelTokenSource
    };

    this.loadReferenceData(cancelTokenSource);
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    this.mounted = false;

    const {cancelTokenSource} = this.state;
    cancelTokenSource && cancelTokenSource.cancel('Operation canceled by user');
  }

  loadReferenceData = async (cancelTokenSource: CancelTokenSource) => {
    const {currentUser} = this.props;
    const mandateId = currentUser!.userConfig.mandateId;

    try {
      const companies = await getAllCompanies(mandateId, cancelTokenSource);
      const countries = await getAllCountries(cancelTokenSource);

      this.setState({
        companies: companies ? mapToDropdownOptionArrayWithSep(companies, ["description", "city"], "id") : [],
        countries: countries ? countries : []
      });

      if (!this.props.selectedCompanyId && companies && companies.length > 0) {
        const selectedCompanyId = companies[0].id;
        this.props.setSelectedCompanyId(selectedCompanyId);
        this.initialSearchValues.companyId = selectedCompanyId;
      }

    } finally {
      this.setState({
        referenceDataLoaded: true
      });
      this.triggerSearch();
    }
  }

  openAddEditModal = (id: number | undefined) => {

    const {cancelTokenSource} = this.state;

    if (id) {
      findDivisionById(id, cancelTokenSource).then(response => {
        this.showModal(id, response);
      }).catch(noop);
    }
  };

  showModal = (id?: number, division?: UpsertDivisionDto) => {
    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        division: division,
        divisionId: id
      }
    });
  };

  hideModal = () => {
    this.setState(({}: State) => ({
      addEditModalOpen: false,
      division: undefined,
      divisionId: undefined
    }));
  };

  onSaveUpsertForm = () => {
    const {searchValues, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        divisionsLoaded: true
      })
    };

    searchDivisions(searchValues, cancelTokenSource)
        .then(response => {
          this.setState({
            foundDivisions: response
          });
        })
        .catch(noop)
        .finally(onFinally);

    this.hideModal();
  };

  handleChange = ({values}: DivisionFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    if (values.companyId) {
      this.props.setSelectedCompanyId(values.companyId);
    }

    this.setState({
      searchValues: values
    });

    this.triggerSearch();
  };

  triggerSearch = debounce((): void => {

    const {companies} = this.state;

    const {searchValues, cancelTokenSource} = this.state;

    const setDivisionLoadingDone = () => this.setDivisionsLoaded(true);

    if (companies && companies.length > 0) {

      this.setState({divisionsLoaded: false});
      searchDivisions(searchValues, cancelTokenSource)
          .then(response =>
              this.setState({
                foundDivisions: response
              })
          )
          .catch(noop)
          .finally(setDivisionLoadingDone);
    } else {
      setDivisionLoadingDone();
    }
  }, 300);

  setDivisionsLoaded = (loaded: boolean) => {
    this.setState({
      divisionsLoaded: loaded
    });
  };

  deleteDivision = (id: number): void => {
    const {cancelTokenSource} = this.state;
    deleteDivision(id, cancelTokenSource).then(response => {
      this.setState(({foundDivisions}: State) => ({
        foundDivisions: foundDivisions.filter((division: DivisionDto) => division.id != response.id)
      }))
    }).catch(noop);
  };

  render(): React.ReactNode {
    const {getMessage} = this.props;
    const {referenceDataLoaded, divisionsLoaded} = this.state;

    if (!referenceDataLoaded) {
      return <></>;
    }

    return (
        <PraxStyledSegment style={{height: "100%"}}>
          <div>
            {this.renderSearchDivisionSegment()}
          </div>
          <StyledViewTableRow>
            {!divisionsLoaded
                ? <LoaderComponent message={getMessage("division.loading")}/>
                : this.renderDivisionsSegment()}
          </StyledViewTableRow>
          {this.renderUpsertDivisionModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchDivisionSegment = (): JSX.Element => {
    const {companies} = this.state;
    return (
        <FinalForm
            onSubmit={() => {
            }}
            initialValues={this.initialSearchValues}
            subscription={{pristine: true, values: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = ({values}: FormRenderProps): React.ReactNode => {

    const {getMessage} = this.props;
    const {companies, foundDivisions} = this.state;

    const numResults = foundDivisions.length;

    const disableAddDivision = !(values.companyId && values.companyId > 0);

    return (
        <PxGrid columns={2}>
          <Grid.Row>
            <Grid.Column width={3}>
              {getMessage("company.title")}:
            </Grid.Column>
            <Grid.Column width={6}>
              <Field
                  name="companyId"
                  component={Select}
                  fluid
                  options={companies}
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("search")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="searchKey"
                  component={Input}
                  autoFocus
                  fluid
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
            </Grid.Column>
            <Grid.Column width={4} verticalAlign={"middle"}>
              <Field
                  name="includesInactive"
                  component={CheckBox}
                  label={getMessage("includingInactive")}
                  toggle={true}
              />
            </Grid.Column>
            <Grid.Column width={9} textAlign="right">
              <AddButton buttonMessage={getMessage("button.add")}
                         disabled={disableAddDivision}
                         onClick={() => this.showModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PxGrid>
    )
  };

  statusCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;
    if (cellData) {
      return getMessage("division.active")
    }
    return getMessage("division.inactive");
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("division.confirmDelete")}
              onConfirm={() => this.deleteDivision(cellData)}
          />
        </React.Fragment>
    );
  };

  divisionsRowGetter = ({index}: any) => {
    const {foundDivisions} = this.state;

    Object.assign(foundDivisions[index], {index: index + 1});

    return foundDivisions[index];
  };

  divisionsRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};

    const {foundDivisions} = this.state;

    const divisionId = foundDivisions[index].id;

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(divisionId)}
        >
          {columns}
        </div>
    );
  };

  renderDivisionsSegment = (): JSX.Element => {

    const {getMessage} = this.props;
    const {foundDivisions} = this.state;
    return (
        <VirtualizedTable
            rowCount={foundDivisions.length}
            rowGetter={this.divisionsRowGetter}
            rowRenderer={this.divisionsRowRenderer}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 200,
                label: (getMessage("status")),
                dataKey: "active",
                cellRenderer: this.statusCellRenderer
              },
              {
                width: 350,
                flexGrow: 2,
                label: (getMessage("division.title")),
                dataKey: "name"
              },
              {
                width: 300,
                label: (getMessage("division.street")),
                dataKey: "street"
              },
              {
                width: 300,
                label: (getMessage("division.address")),
                dataKey: "zip"
              },
              {
                width: 300,
                flexGrow: 1,
                label: (getMessage("division.city")),
                dataKey: "city"
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />

    );
  };


  renderUpsertDivisionModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, division, divisionId, countries, searchValues} = this.state;

    const companyId = searchValues.companyId;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={() => this.hideModal()}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("division.title")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertDivisionForm
                companyId={companyId}
                division={division}
                divisionId={divisionId}
                countries={countries}
                onCancel={this.hideModal}
                onSave={this.onSaveUpsertForm}
            />
          </FormModalContentContainer>
        </Modal>
    );
  };

}

export default withMessages(withGuarantorsContext(withAuthContext(DivisionsView)));
